import React, { useMemo } from 'react';
import { MapContainer, TileLayer, CircleMarker, Polyline, Tooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

/** Génère un arc courbe (bézier quadratique) entre deux points [lat, lon]. */
function arc(a, b, bend = 0.18) {
  const [lat1, lon1] = a;
  const [lat2, lon2] = b;
  const mlat = (lat1 + lat2) / 2;
  const mlon = (lon1 + lon2) / 2;
  const dx = lat2 - lat1;
  const dy = lon2 - lon1;
  const clat = mlat - dy * bend;
  const clon = mlon + dx * bend;
  const pts = [];
  for (let t = 0; t <= 1.0001; t += 0.05) {
    const x = (1 - t) * (1 - t) * lat1 + 2 * (1 - t) * t * clat + t * t * lat2;
    const y = (1 - t) * (1 - t) * lon1 + 2 * (1 - t) * t * clon + t * t * lon2;
    pts.push([x, y]);
  }
  return pts;
}

/**
 * Carte interactive des flux origine-destination.
 * Points = arrondissements (taille ∝ activité sortante), arcs = corridors (épaisseur ∝ volume).
 */
export default function FlowMap({ data }) {
  const { zones, byId, flows, maxVol, out, maxOut, center } = useMemo(() => {
    const z = (data?.zones || []).filter((x) => x.lat != null && x.lon != null);
    const idx = Object.fromEntries(z.map((x) => [x.id, x]));
    const f = (data?.flows || []).filter((fl) => idx[fl.origin] && idx[fl.destination]);
    const mv = f.length ? Math.max(...f.map((fl) => fl.volume)) : 1;
    const o = {};
    f.forEach((fl) => { o[fl.origin] = (o[fl.origin] || 0) + fl.volume; });
    const mo = Math.max(1, ...Object.values(o));
    const c = z.length
      ? [z.reduce((s, x) => s + x.lat, 0) / z.length, z.reduce((s, x) => s + x.lon, 0) / z.length]
      : [14.72, -17.45];
    return { zones: z, byId: idx, flows: f, maxVol: mv, out: o, maxOut: mo, center: c };
  }, [data]);

  return (
    <MapContainer center={center} zoom={11} scrollWheelZoom style={{ height: '100%', width: '100%' }}>
      <TileLayer
        url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
        attribution='&copy; OpenStreetMap, &copy; CARTO'
        subdomains="abcd"
        maxZoom={19}
      />

      {flows.map((f, i) => {
        const a = byId[f.origin];
        const b = byId[f.destination];
        const ratio = f.volume / maxVol;
        return (
          <Polyline
            key={`${f.origin}-${f.destination}-${i}`}
            positions={arc([a.lat, a.lon], [b.lat, b.lon])}
            pathOptions={{ color: '#0d9488', weight: 1 + ratio * 6, opacity: 0.25 + 0.5 * ratio }}
          >
            <Tooltip sticky>{a.nom} → {b.nom} : {f.volume.toLocaleString('fr-FR')}</Tooltip>
          </Polyline>
        );
      })}

      {zones.map((z) => {
        const r = 6 + Math.sqrt((out[z.id] || 0) / maxOut) * 16;
        return (
          <CircleMarker
            key={z.id}
            center={[z.lat, z.lon]}
            radius={r}
            pathOptions={{ color: '#0f766e', fillColor: '#10b981', fillOpacity: 0.75, weight: 2 }}
          >
            <Tooltip direction="top" offset={[0, -4]}>
              <strong>{z.nom}</strong>
            </Tooltip>
          </CircleMarker>
        );
      })}
    </MapContainer>
  );
}
